import React, { useMemo } from 'react';
import { CalculationResult } from '../utils/roiCalculations';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsSummaryProps {
  results: CalculationResult;
  halfSales?: boolean;
  doubleAttrition?: boolean;
}
export function ResultsSummary({
  results,
  halfSales = false,
  doubleAttrition = false
}: ResultsSummaryProps) {
  // Calculate totals directly from rows to ensure accuracy
  const calculatedTotals = useMemo(() => {
    // Get Month 12 row (index 12, since index 0 is Build)
    const month12Row = results.rows[12];
    if (!month12Row) {
      // Fallback if Month 12 doesn't exist
      return {
        totalGrossProfit: 0,
        totalNetProfit: 0,
        roi: 0
      };
    }
    // Calculate cumulative gross profit up to Month 12
    let cumGrossProfit = 0;
    let cumTotalCost = 0;
    for (let i = 0; i <= 12; i++) {
      cumGrossProfit += results.rows[i].grossProfit;
      cumTotalCost += results.rows[i].totalCost;
    }
    // Apply scenario adjustments
    if (halfSales) {
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    if (doubleAttrition) {
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    // Calculate ROI: (Cumulative Gross Profit / Cumulative Total Investment) * 100
    const roi = cumTotalCost > 0 ? cumGrossProfit / cumTotalCost * 100 : 0;
    const totalNetProfit = cumGrossProfit - cumTotalCost;
    return {
      totalGrossProfit: cumGrossProfit,
      totalNetProfit,
      roi
    };
  }, [results.rows, halfSales, doubleAttrition]);
  const roiClass = calculatedTotals.roi >= 0 ? 'text-green-600' : 'text-red-600';
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-id="element-449">
      {/* Hires / Month */}
      <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200" data-id="element-450">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-451">
          Hires / Month (estimated)
        </div>
        <div className="text-2xl md:text-3xl font-bold text-navy" data-id="element-452">
          {results.hiresPerMonth.toLocaleString()} / mo
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-453">
          Launched: {results.launchedPerMonth.toFixed(1)} / mo
        </div>
      </div>

      {/* Gross Profit */}
      <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200" data-id="element-454">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-455">
          12-Month Gross Profit
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-456">
              (Scenario Active)
            </span>}
        </div>
        <div className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-457">
          {fmtMoney(calculatedTotals.totalGrossProfit)}
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-458">
          GP / Sale: {fmtMoney(results.gpPerSale)}
        </div>
      </div>

      {/* ROI */}
      <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200" data-id="element-459">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-460">
          12-Month ROI
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-461">
              (Scenario Active)
            </span>}
        </div>
        <div className={`text-2xl md:text-3xl font-bold ${roiClass}`} data-id="element-462">
          {Math.round(calculatedTotals.roi)}%
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-463">
          Net Profit: {fmtMoney(calculatedTotals.totalNetProfit)}
        </div>
      </div>
    </div>;
}